import { storeToRefs } from 'pinia';
import { FetchError } from 'ofetch';
import { AssortmentService } from './assortment';
import { AuthService } from './auth';
import { CampaignService } from './campaign';
import { CartService } from './cart';
import { LocationService } from './location';
import { OrderService } from './order';
import { ProductService } from './product';
import { SearchService } from './search';
import { StockService } from './stock';
import { PriceService } from './price';
import { ApiGatewayService } from './apigateway';
import { SequenceService } from './sequence';
import { QuoteService } from './quote';
import { createHttpClient } from '@/utils/HttpClient';
import type { HookData, ErrorHookData, HttpClientOptions } from '@/utils/HttpClient';
import { useUiStore, useUserStore, useCustomerStore, useCartStore } from '@/stores';
import { events } from '@/stores/state-machines/cartMachine.js';

interface ErrorResponse {
  status: number;
  data: string;
}

interface Error {
  response: ErrorResponse;
  message?: string;
}

export type ApiResponse<T, E = FetchError> = Promise<HttpResponse<T, E>>;

export default defineNuxtPlugin((nuxtApp) => {
  const { $sentry, $httpsProxyAgentFetch } = nuxtApp;
  const config = useRuntimeConfig();
  const { showMessage: showMessageUi, setStartMessage } = useUiStore();
  const { setCartStatus } = useCartStore();
  const userStore = useUserStore();
  const { currentToken, refreshToken } = storeToRefs(userStore);
  const { clear, setIsAuthenticating, login } = userStore;

  const customerStore = useCustomerStore();
  const { currentCustomerId } = storeToRefs(customerStore);

  const showMessage = (message: string) => {
    showMessageUi({ text: message });
  };

  const showErrorMessage = (error: Error, notRespondingText: string) => {
    let generalErrorMessage = 'errors.unknown_error_occurred';
    if (!error.response?.status) {
      generalErrorMessage = notRespondingText || generalErrorMessage;
    } else if (error.response.data === 'Invalid token') {
      generalErrorMessage = 'errors.invalid_token';
    } else if (error.response.status >= 500) {
      generalErrorMessage = 'errors.internal_server_error';
    } else if (error.response.status === 403) {
      generalErrorMessage = 'errors.forbidden_action';
    } else if (error.response.status === 404) {
      generalErrorMessage = 'errors.not_found';
    }

    showMessage(generalErrorMessage);
  };

  const isTokenExpired = (token: string) => {
    return !isTokenValid(token, config.public.users.tokenRefreshMargin);
  };

  const logger = (error: Error) => {
    if (error?.message?.startsWith('request aborted')) {
      // No need to log aborted requests
      return;
    }
    const outputError = error.response || error;
    $sentry.captureException(outputError);
    // eslint-disable-next-line no-console
    console.error(outputError);
  };

  const logout = () => {
    // we cant call the logout action since we probably dont have a valid token at this point
    clear();
    setIsAuthenticating(false);
    setStartMessage({ text: 'please_login' });
    location.reload();
  };

  const commonHooks = {
    async onBeforeRequest({ requestOptions, state }: HookData) {
      if (!process.server) {
        const authorizationHeader = currentToken.value;
        const token = authorizationHeader.replace('Bearer ', '');
        if (isTokenExpired(token) && refreshToken.value) {
          state.unresolvedRefresh = login({
            refreshToken: refreshToken.value,
            customerId: currentCustomerId.value || undefined,
          });
        }

        await state.unresolvedRefresh;
        state.unresolvedRefresh = null;

        (requestOptions as HttpClientOptions).headers = {
          ...(requestOptions?.headers as Headers),
          Authorization: currentToken.value,
        };
      }
    },
    onRequestError({ error, requestOptions, errorMessage }: ErrorHookData) {
      if (!requestOptions?.omitErrorMessage && errorMessage) showErrorMessage(error as Error, errorMessage);
      logger(error as Error);
      if ((error as Error).response?.status === 401) {
        controller.abort();
        logout();
      }
    },
  };

  const proxyConfig = config.public.brand !== 'dahl' && process.server ? { httpsAgent: $httpsProxyAgentFetch } : {};

  const controller = new AbortController();

  const cartService = CartService(
    createHttpClient(
      config.public.cartService,
      controller,
      {
        ...commonHooks,
        onRequestError(hookData: ErrorHookData) {
          setCartStatus(events.ERROR);
          commonHooks.onRequestError(hookData);
        },
      },
      { errorMessage: 'errors.cart_service_not_available' },
    ),
  );
  const orderService = OrderService(
    createHttpClient(config.public.orderService, controller, commonHooks, {
      errorMessage: 'errors.order_service_not_available',
    }),
  );
  const authService = AuthService(
    createHttpClient(config.authService || config.public.authService, controller, commonHooks, {
      errorMessage: 'errors.auth_service_not_available',
    }),
  );
  const stockService = StockService(createHttpClient(config.public.stockService, controller, commonHooks));
  const priceService = PriceService(createHttpClient(config.public.priceService, controller, commonHooks));
  const productService = ProductService(
    createHttpClient(config.productService || config.public.productService, controller, commonHooks, proxyConfig),
    $sentry,
  );
  const locationService = LocationService(createHttpClient(config.public.locationService, controller, commonHooks));
  const campaignService = CampaignService(
    createHttpClient(config.campaignService || config.public.campaignService, controller, commonHooks),
  );
  const assortmentService = AssortmentService(
    createHttpClient(config.public.assortmentService, controller, commonHooks),
  );
  const searchService = SearchService(
    createHttpClient(
      config.public.searchService,
      controller,
      {},
      {
        httpsAgent: process.server ? $httpsProxyAgentFetch : undefined,
      },
    ),
  );

  const sequenceService = SequenceService(
    createHttpClient(
      config.public.brand === 'dahl' ? config.public.pondUrl : config.public.productService,
      controller,
      {},
      {
        httpsAgent: process.server ? $httpsProxyAgentFetch : undefined,
      },
    ),
  );

  const quoteService = QuoteService(
    createHttpClient(config.public.quoteService, controller, {
      ...commonHooks,
      onRequestError(hookData: ErrorHookData) {
        console.log(hookData);
        showMessageUi({
          // TODO add proper error handling. This should be enough to catch all the BE errors for now, so we can get them fixed
          text: `${JSON.stringify(hookData.error)} ${hookData.error?.message ?? ''} ${hookData.errorMessage ?? ''} ${
            hookData.error?.data ?? ''
          }`,
        });
        commonHooks.onRequestError(hookData);
      },
    }),
  );

  const apiGateway = ApiGatewayService(
    createHttpClient(config.apiGateway || config.public.apiGateway, controller, commonHooks),
  );

  return {
    provide: {
      assortmentService,
      apiGateway,
      authService,
      campaignService,
      cartService,
      locationService,
      orderService,
      productService,
      searchService,
      stockService,
      priceService,
      sequenceService,
      quoteService,
    },
  };
});
